import { useCallback, useEffect, useRef, useState } from 'react';

import clinicalNoteService from './clinicalNoteService.js';

/**
 * Clinical-note validation sections. These codes must match the backend's
 * ValidationIssue.sectionCode values.
 */
export const SECTION_CODES = Object.freeze([
  'EVALUATION_HISTORY',
  'PAIN',
  'ROM',
  'STRENGTH',
  'GOAL',
  'SPECIAL_TEST',
  'PALPATION',
  'CONTENT',
  'TREATMENT',
  'PLAN_OF_CARE',
  'ATTACHED_DAILY',
]);

const DEFAULT_VALUE_CODE = 'DEFAULT_VALUE';

function createStatuses(level, label) {
  return Object.fromEntries(
    SECTION_CODES.map((code) => [code, { level, label }])
  );
}

/**
 * Only an explicit, backend-provided section completion result is evidence
 * of completion. An empty list of issues is NOT proof that a section was
 * filled in; it can also mean optional, inactive or unvalidated.
 *
 * This reads optional completion metadata without requiring new endpoints.
 * If the current backend does not send it, the UI stays neutral.
 */
function isExplicitlyComplete(validation, sectionCode) {
  const entry = validation?.sectionStatuses?.[sectionCode];
  if (entry === true || entry === 'COMPLETE' || entry === 'COMPLETED') {
    return true;
  }
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  return (
    entry.complete === true ||
    entry.completed === true ||
    entry.status === 'COMPLETE' ||
    entry.status === 'COMPLETED'
  );
}

/**
 * Prioritize blocking validation issues, then warnings. Draft-time missing
 * values may be allowed by the backend; this hook does not block editing.
 */
export function buildClinicalSectionStatus(issues, explicitlyComplete = false) {
  const blocking = issues.filter((issue) => issue?.blocking === true);
  if (blocking.length > 0) {
    return {
      level: 'red',
      label: blocking.length === 1
        ? '1 required issue'
        : `${blocking.length} required issues`,
    };
  }

  const warnings = issues.filter((issue) => issue?.warning === true);
  if (warnings.length > 0) {
    const defaultsOnly = warnings.every(
      (issue) => issue?.issueCode === DEFAULT_VALUE_CODE
    );
    return {
      level: 'yellow',
      label: defaultsOnly
        ? 'Default value / wording'
        : warnings.length === 1
          ? '1 warning'
          : `${warnings.length} warnings`,
    };
  }

  return explicitlyComplete
    ? { level: 'green', label: 'Complete' }
    : { level: 'neutral', label: 'No issues reported' };
}

/**
 * Read-only section status hook. Validation is backend-authoritative; the
 * front end does not claim completion from a lack of reported issues.
 *
 * refreshSectionStatuses() remains the public callback used by form wrappers.
 * Older requests cannot overwrite a newer visit's status information.
 */
export default function useClinicalSectionStatuses(clinicalNoteId) {
  const [statuses, setStatuses] = useState(() =>
    createStatuses('neutral', 'Checking…')
  );
  const [loading, setLoading] = useState(Boolean(clinicalNoteId));
  const [validationError, setValidationError] = useState('');
  const [lastValidatedAt, setLastValidatedAt] = useState(null);

  const requestSequenceRef = useRef(0);
  const noteIdRef = useRef(clinicalNoteId);
  const activeRef = useRef(false);
  noteIdRef.current = clinicalNoteId;

  const refreshSectionStatuses = useCallback(async () => {
    const requestNoteId = clinicalNoteId;
    const sequence = ++requestSequenceRef.current;

    if (!requestNoteId) {
      if (activeRef.current && sequence === requestSequenceRef.current) {
        setStatuses(createStatuses('neutral', 'No note selected'));
        setLoading(false);
        setValidationError('');
        setLastValidatedAt(null);
      }
      return null;
    }

    if (activeRef.current) {
      setLoading(true);
      setValidationError('');
    }

    try {
      const validation = await clinicalNoteService.validateClinicalNote(requestNoteId);
      if (
        !activeRef.current ||
        sequence !== requestSequenceRef.current ||
        requestNoteId !== noteIdRef.current
      ) {
        return null;
      }

      // Unknown/missing validation payloads must not yield green badges.
      if (!validation || !Array.isArray(validation.issues)) {
        throw new Error('Clinical-note validation returned no issues list.');
      }

      const bySection = Object.fromEntries(
        SECTION_CODES.map((code) => [code, []])
      );
      for (const issue of validation.issues) {
        const sectionCode = issue?.sectionCode;
        if (Object.prototype.hasOwnProperty.call(bySection, sectionCode)) {
          bySection[sectionCode].push(issue);
        }
      }

      const nextStatuses = {};
      for (const code of SECTION_CODES) {
        nextStatuses[code] = buildClinicalSectionStatus(
          bySection[code],
          isExplicitlyComplete(validation, code)
        );
      }

      setStatuses(nextStatuses);
      setLastValidatedAt(new Date());
      return validation;
    } catch (error) {
      if (
        activeRef.current &&
        sequence === requestSequenceRef.current &&
        requestNoteId === noteIdRef.current
      ) {
        setStatuses(createStatuses('neutral', 'Status unavailable'));
        setValidationError(
          error?.message || 'Clinical-note validation is unavailable.'
        );
      }
      return null;
    } finally {
      if (
        activeRef.current &&
        sequence === requestSequenceRef.current &&
        requestNoteId === noteIdRef.current
      ) {
        setLoading(false);
      }
    }
  }, [clinicalNoteId]);

  useEffect(() => {
    activeRef.current = true;
    setStatuses(createStatuses('neutral', clinicalNoteId ? 'Checking…' : 'No note selected'));
    setLastValidatedAt(null);
    setValidationError('');
    void refreshSectionStatuses();

    return () => {
      activeRef.current = false;
      ++requestSequenceRef.current;
    };
  }, [clinicalNoteId, refreshSectionStatuses]);

  return {
    statuses,
    refreshSectionStatuses,
    loading,
    validationError,
    lastValidatedAt,
  };
}
